"use client"

import { useMemo, useState } from "react"
import { CalendarDays, ChevronDown } from "lucide-react"
import CalendarModal from "@/components/calendar-modal"
import { useTranslation } from "react-i18next"
import { toEthiopian } from "ethiopian-calendar-new"
import { getEthiopianMonth } from "@/lib/ethiopian-date-conversion"
import { useBookingStore } from "@/store/booking-store"

function toYmd(d: Date) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export default function ScheduleHeader({
  branchId,
  serviceId,
  appointmentDate,
  today,
  calendarType,
  setCalendarType,
}: {
  branchId: string
  serviceId: string
  appointmentDate: string
  today: Date
  calendarType: 'GC' | 'EC'
  setCalendarType: (type: 'GC' | 'EC') => void
}) {
  const [open, setOpen] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const { setBookingState } = useBookingStore()
  const monthLabel = useMemo(() => new Date(appointmentDate).toLocaleString(undefined, { month: "long", year: "numeric" }), [appointmentDate])
  const amMonthLabel = useMemo(() => {
    const date = new Date(appointmentDate);
    const { year, month } = toEthiopian(date.getFullYear(), date.getMonth() + 1, date.getDate());
    return `${getEthiopianMonth(month)} ${year}`;
  }, [appointmentDate]);


  const { t } = useTranslation();

  const handleCalendarTypeChange = (type: 'GC' | 'EC') => {
    setCalendarType(type);
    setBookingState({ calendarType: type });
  };

  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <div className="flex flex-col">
          <div className="text-lg font-bold text-gray-900 tracking-tight">
            {calendarType === "EC" ? amMonthLabel : monthLabel}
          </div>
          <div className="text-xs font-medium text-gray-500">{t("selectDate")}</div>
        </div>
        
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full bg-gray-100 text-xs font-bold text-gray-700 hover:bg-gray-200 transition-colors"
            >
                {calendarType === 'EC' ? 'Ethiopian' : 'Gregorian'}
                <ChevronDown size={14} />
            </button>
            
            {dropdownOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setDropdownOpen(false)} />
                  <div className="absolute top-full right-0 mt-1 w-32 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-20">
                      <button 
                          onClick={() => { handleCalendarTypeChange('GC'); setDropdownOpen(false); }}
                          className={`w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 ${calendarType === 'GC' ? 'text-pink-600 bg-pink-50' : 'text-gray-700'}`}
                      >
                          Gregorian
                      </button>
                      <button 
                          onClick={() => { handleCalendarTypeChange('EC'); setDropdownOpen(false); }}
                          className={`w-full text-left px-4 py-2 text-xs font-medium hover:bg-gray-50 ${calendarType === 'EC' ? 'text-pink-600 bg-pink-50' : 'text-gray-700'}`}
                      >
                          Ethiopian
                      </button>
                  </div>
                </>
            )}
          </div>

          <button 
            onClick={() => setOpen(true)} 
            className="w-10 h-10 flex items-center justify-center rounded-full bg-pink-50 text-pink-600 hover:bg-pink-100 hover:scale-105 transition-all duration-300 active:scale-95 shadow-sm hover:shadow-pink-100"
          >
            <CalendarDays className="w-5 h-5" strokeWidth={2} />
          </button>
        </div>
      </div>

      <CalendarModal
        open={open}
        onClose={() => setOpen(false)}
        branchId={branchId}
        serviceId={serviceId}
        initialDate={appointmentDate}
      />
    </>
  )
}


